/**
 * Forecast page cards: "Expected deliveries" grouped by LPO (or flat by part), the "Order now"
 * suggestions with '—' / "Supplier missing" / "default" lead time instead of blank cells, and the
 * amber "Data gaps" chip that lists the parts whose catalog data the numbers depend on.
 * Wave 3 (23 #9): a late LPO's group row has "Chase supplier"; once chased it says "ETA asked 2 d ago · no reply ·
 * Chase again", and after the supplier's new date "New ETA 15 Oct · was 12 Mar".
 */
import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Mail, PackageSearch, SearchX, Sparkles, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ageDays, formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  CardFoot, CardHead, DateCell, Dash, LinkButton, MissingText, QtyProgress, Segmented, SfCard, TableScroll,
  numClass, plural, relativeDayText, tableClass, tdClass, thClass, useIsPhone,
} from './SfUi';
import { groupByLpo, lineTotals } from './forecastModel';
import { chaseState } from './chaseModel';

/** The group row's "Chase supplier" / "ETA asked 2 d ago · no reply · Chase again" (23 #9). */
export function ChaseCell({ group, onChase, now, compact = false }) {
  if (!onChase || !group.lpoId) return null;
  const state = chaseState({ ...group.chase, expected: group.expected }, { now });
  const chase = () => onChase(group);
  if (state.state === 'none') {
    if (!(group.daysLate > 0)) return null;
    return compact ? (
      <LinkButton onClick={chase} data-testid={`forecast-chase-${group.lpoId}`}><Sparkles className="h-3.5 w-3.5" aria-hidden="true" />Chase supplier</LinkButton>
    ) : (
      <Button variant="outline" size="sm" onClick={chase} data-testid={`forecast-chase-${group.lpoId}`}>
        <Sparkles aria-hidden="true" /> Chase supplier
      </Button>
    );
  }
  return (
    <span className={cn('inline-flex flex-col leading-tight', compact ? 'items-start' : 'items-end')} data-testid={`forecast-chase-state-${group.lpoId}`}>
      <span className="inline-flex items-center gap-1 whitespace-nowrap text-[12px] font-semibold text-ink-2">
        <Mail className="h-3.5 w-3.5 text-status-green-ink" aria-hidden="true" />{state.text}
      </span>
      <span className="whitespace-nowrap text-[11.5px] text-ink-3">
        {state.sub ? <>{state.sub}<span className="text-ink-4" aria-hidden="true"> · </span></> : null}
        <LinkButton onClick={chase} className="text-[11.5px]" data-testid={`forecast-chase-${group.lpoId}`}>{state.state === 'asked' ? 'Chase again' : 'Chase'}</LinkButton>
      </span>
    </span>
  );
}

function RemainingCell({ value }) {
  return (
    <span className={cn('font-bold tabular-nums', value > 0 ? 'text-status-amber-ink' : 'text-status-green-ink')}>
      {formatNumber(value)}
    </span>
  );
}

function LpoGroupLabel({ group, open, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex min-w-0 items-center gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />}
      <span className="shrink-0 font-mono text-[13px] font-bold text-ink">{group.lpoNumber || 'No LPO number'}</span>
      <span className="min-w-0 truncate text-[13px] font-semibold text-ink-2">
        <span className="text-ink-4" aria-hidden="true"> · </span>
        {group.supplier || <MissingText>Supplier missing</MissingText>}
        <span className="text-ink-4" aria-hidden="true"> · </span>
        {group.projectName ? <span className="font-medium">{group.projectName}</span> : <MissingText>No project linked</MissingText>}
        {!open ? <span className="font-medium text-ink-3"> · {plural(group.lines.length, 'line')}</span> : null}
      </span>
    </button>
  );
}

/** "12 Mar · 203 d late" with the lateness in red (phone lists). */
export function ShortDue({ value, now }) {
  if (!value) return <MissingText>No date</MissingText>;
  const { text, tone } = relativeDayText(ageDays(value, { now }));
  return (
    <span className="whitespace-nowrap">
      {formatDate(value, { year: 'auto', now })}
      {text ? <span className={cn(tone === 'red' && 'font-semibold text-status-red-ink', tone === 'amber' && 'font-semibold text-status-amber-ink')}> · {text}</span> : null}
    </span>
  );
}

function PhoneLine({ line, showLpo, now }) {
  return (
    <li className="flex items-center gap-3 px-4 py-2.5">
      <span className="min-w-0 flex-1">
        <span className="block font-mono text-[12.5px] text-ink">{line.part_number}</span>
        <span className="block truncate text-[12px] text-ink-3">
          {showLpo ? <><span className="font-mono">{line.lpo_number}</span> · <ShortDue value={line.expected} now={now} /></> : (line.description || '—')}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block"><RemainingCell value={line.remaining} /> <span className="text-[11.5px] text-ink-3">left</span></span>
        <span className="block text-[11.5px] tabular-nums text-ink-3">{formatNumber(Math.min(line.delivered, line.ordered))}/{formatNumber(line.ordered)} delivered</span>
      </span>
    </li>
  );
}

export function ExpectedDeliveriesCard({ lines, filtered, onClearFilters, now, onChase }) {
  const isPhone = useIsPhone();
  const [mode, setMode] = useState('lpo');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const groups = useMemo(() => groupByLpo(lines), [lines]);
  const totals = useMemo(() => lineTotals(lines), [lines]);
  const showActions = mode === 'lpo' && Boolean(onChase);
  const toggle = (key) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <SfCard data-testid="expected-deliveries-table">
      <CardHead
        title="Expected deliveries"
        sub={`${plural(totals.lines, 'line')} · ${mode === 'lpo' ? 'grouped by LPO' : 'by part, late first'}`}
        right={(
          <Segmented
            size="sm"
            ariaLabel="Group expected deliveries"
            value={mode}
            onChange={setMode}
            options={[{ key: 'lpo', label: 'By LPO', testId: 'expected-by-lpo' }, { key: 'part', label: 'By part', testId: 'expected-by-part' }]}
          />
        )}
      />
      {!lines.length ? (
        filtered ? (
          <EmptyState icon={SearchX} title="No deliveries match" text="Try another horizon, search or filter."
            action={onClearFilters ? <Button variant="outline" size="sm" onClick={onClearFilters}>Show all open</Button> : null} />
        ) : (
          <EmptyState icon={PackageSearch} title="Nothing is waiting to be delivered" text="Lines appear here while an LPO still has quantities to come." />
        )
      ) : isPhone ? (
        mode === 'lpo' ? (
          <div>
            {groups.map((group) => {
              const open = !collapsed.has(group.key);
              return (
                <section key={group.key} className="border-b border-line-2 last:border-b-0" data-testid={`expected-group-${group.lpoNumber}`}>
                  <div className="space-y-1.5 bg-surface-2 px-4 py-2.5">
                    <LpoGroupLabel group={group} open={open} onToggle={() => toggle(group.key)} />
                    <div className="flex items-center justify-between gap-3 pl-5 text-[12px] text-ink-2">
                      <QtyProgress done={group.delivered} total={group.ordered} barClassName="w-[64px]" />
                      <ShortDue value={group.expected} now={now} />
                    </div>
                    {onChase ? <div className="pl-5"><ChaseCell group={group} onChase={onChase} now={now} compact /></div> : null}
                  </div>
                  {open ? <ul className="divide-y divide-line-2">{group.lines.map((line) => <PhoneLine key={line.key} line={line} now={now} />)}</ul> : null}
                </section>
              );
            })}
          </div>
        ) : (
          <ul className="divide-y divide-line-2">{lines.map((line) => <PhoneLine key={line.key} line={line} showLpo now={now} />)}</ul>
        )
      ) : (
        <TableScroll>
          <table className={cn(tableClass, 'min-w-[930px] table-fixed')}>
            <colgroup>
              <col style={{ width: 170 }} /><col /><col style={{ width: 190 }} /><col style={{ width: 110 }} /><col style={{ width: 70 }} /><col style={{ width: 150 }} />
              {showActions ? <col style={{ width: 190 }} /> : null}
            </colgroup>
            <thead>
              <tr>
                <th className={thClass}>{mode === 'lpo' ? 'Part' : 'Part / LPO'}</th>
                <th className={thClass}>Description</th>
                <th className={thClass}>Delivered</th>
                <th className={cn(thClass, 'text-right')}>Remaining</th>
                <th className={thClass}>Unit</th>
                <th className={thClass}>Expected</th>
                {showActions ? <th className={thClass}><span className="sr-only">Supplier follow-up</span></th> : null}
              </tr>
            </thead>
            <tbody>
              {mode === 'lpo' ? groups.map((group) => {
                const open = !collapsed.has(group.key);
                return (
                  <Fragment key={group.key}>
                    <tr className="bg-surface-2" data-testid={`expected-group-${group.lpoNumber}`}>
                      <td colSpan={2} className={cn(tdClass, 'h-11')}>
                        <LpoGroupLabel group={group} open={open} onToggle={() => toggle(group.key)} />
                      </td>
                      <td className={cn(tdClass, 'h-11')}><QtyProgress done={group.delivered} total={group.ordered} barClassName="w-[80px]" label={`${group.lpoNumber}: ${formatNumber(group.delivered)} of ${formatNumber(group.ordered)} delivered`} /></td>
                      <td className={cn(tdClass, numClass, 'h-11')}><RemainingCell value={group.remaining} /></td>
                      <td className={cn(tdClass, 'h-11')} />
                      <td className={cn(tdClass, 'h-11 py-1.5')}><DateCell value={group.expected} now={now} emptyLabel="No date" /></td>
                      {showActions ? <td className={cn(tdClass, 'h-11 py-1.5 text-right')}><ChaseCell group={group} onChase={onChase} now={now} /></td> : null}
                    </tr>
                    {open ? group.lines.map((line) => (
                      <tr key={line.key} className="hover:bg-surface-hover">
                        <td className={cn(tdClass, 'first:pl-10 sm:first:pl-10')}><span className="font-mono text-[12.5px] text-ink">{line.part_number}</span></td>
                        <td className={cn(tdClass, 'truncate text-ink-2')}>{line.description || <Dash />}</td>
                        <td className={tdClass}><QtyProgress done={Math.min(line.delivered, line.ordered)} total={line.ordered} barClassName="w-[80px]" label={`${line.part_number}: ${formatNumber(line.delivered)} of ${formatNumber(line.ordered)} delivered`} /></td>
                        <td className={cn(tdClass, numClass)}><RemainingCell value={line.remaining} /></td>
                        <td className={cn(tdClass, 'text-[12.5px] text-ink-2')}>{line.unit || <Dash label="No unit" />}</td>
                        <td className={tdClass} />
                        {showActions ? <td className={tdClass} /> : null}
                      </tr>
                    )) : null}
                  </Fragment>
                );
              }) : lines.map((line) => (
                <tr key={line.key} className="hover:bg-surface-hover">
                  <td className={tdClass}>
                    <span className="block font-mono text-[12.5px] text-ink">{line.part_number}</span>
                    <span className="block truncate text-[12px] text-ink-3"><span className="font-mono text-[11.5px]">{line.lpo_number}</span> · {line.supplier || 'Supplier missing'}</span>
                  </td>
                  <td className={cn(tdClass, 'truncate text-ink-2')}>{line.description || <Dash />}</td>
                  <td className={tdClass}><QtyProgress done={Math.min(line.delivered, line.ordered)} total={line.ordered} barClassName="w-[80px]" /></td>
                  <td className={cn(tdClass, numClass)}><RemainingCell value={line.remaining} /></td>
                  <td className={cn(tdClass, 'text-[12.5px] text-ink-2')}>{line.unit || <Dash label="No unit" />}</td>
                  <td className={cn(tdClass, 'py-1.5')}><DateCell value={line.expected} now={now} emptyLabel="No date" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {lines.length ? (
        <CardFoot>
          <span>Total · {plural(totals.lines, 'line')} on {plural(totals.lpos, 'LPO')}</span>
          <span className="tabular-nums">
            <b className="font-semibold text-ink">{formatNumber(totals.delivered)} of {formatNumber(totals.ordered)} pcs delivered</b>
            {' · '}{formatNumber(totals.remaining)} remaining
          </span>
        </CardFoot>
      ) : null}
    </SfCard>
  );
}

/** "Order now" moved to its own file in wave 2 (Create LPO, Why short?) — re-exported for existing imports. */
export { OrderNowCard } from './OrderNowCard';

/** Amber "Data gaps N" chip → list of parts with what is missing, a filter toggle and a link to the Catalog. */
export function DataGapsChip({ gaps, active, onToggle }) {
  const [open, setOpen] = useState(false);
  if (!gaps.length) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="forecast-data-gaps"
          className={cn(
            'inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-full border px-[11px] text-[12.5px] font-semibold text-status-amber-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            active ? 'border-[color:var(--status-amber)] bg-status-amber-bg' : 'border-[color:var(--status-amber-bg)] bg-status-amber-bg hover:border-[color:var(--status-amber)]'
          )}
        >
          <TriangleAlert className="h-[13px] w-[13px]" aria-hidden="true" />
          Data gaps <span className="tabular-nums">{formatNumber(gaps.length)}</span>
          {active ? <span className="sr-only">(showing only these parts)</span> : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" collisionPadding={16} className="w-[340px] max-w-[calc(100vw-32px)] rounded-xl border-line bg-surface p-0 text-ink shadow-overlay">
        <div className="border-b border-line-2 px-3.5 py-3">
          <p className="font-heading text-[13.5px] font-bold text-ink">Catalog data missing</p>
          <p className="mt-0.5 text-[12px] text-ink-3">Order-by dates use a 14-day default lead time until the catalog has one.</p>
        </div>
        <ul className="max-h-64 divide-y divide-line-2 overflow-y-auto">
          {gaps.map((gap) => (
            <li key={gap.partNumber} className="px-3.5 py-2">
              <div className="flex min-w-0 items-baseline gap-2">
                <span className="shrink-0 font-mono text-[12.5px] font-semibold text-ink">{gap.partNumber}</span>
                <span className="min-w-0 truncate text-[12px] text-ink-3">{gap.description}</span>
              </div>
              <p className="text-[12px] font-semibold text-status-amber-ink">{gap.issues.join(' · ')}</p>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between gap-2 border-t border-line-2 px-3.5 py-2.5">
          <Button variant="outline" size="sm" onClick={() => { onToggle(); setOpen(false); }} data-testid="forecast-data-gaps-toggle">
            {active ? 'Show all parts' : 'Show only these parts'}
          </Button>
          <Link to="/inventory?tab=catalog" className="text-[12.5px] font-semibold text-brand-ink hover:underline">Open Catalog</Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
